import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import axios from 'axios'
import { useAuthStore } from '../../../../../stores/auth/useAuthStore'

interface ISharedAttachmentProps {
    attachment: {
        name: string
        mediaType: string
        byteSize: number
        fetchPath: string
        unavailable?: boolean
    }
    sent: boolean
}

/**
 * An attachment on a shared-inbox message. Its bytes sit behind Unipile's API key, so they
 * come through our API with the user's own token — which an <img src> cannot send. Images
 * and voice notes are fetched as they render; anything else only when clicked.
 */
const SharedAttachment: React.FC<ISharedAttachmentProps> = ({ attachment, sent }) => {
    const { store: { authData: { accessToken, refreshToken } } } = useAuthStore()
    const [objectUrl, setObjectUrl] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [failed, setFailed] = useState(!!attachment.unavailable)
    const objectUrlRef = useRef<string | null>(null)

    const kind = attachment.mediaType?.split('/')?.[0]
    const inline = kind === 'image' || kind === 'audio'

    const fetchBlobUrl = async () => {
        if (objectUrlRef.current) return objectUrlRef.current
        const response = await axios.get(attachment.fetchPath, {
            baseURL: process.env.REACT_APP_API_URL,
            responseType: 'blob',
            headers: {
                ...(accessToken ? { Authorization: accessToken } : {}),
                ...(refreshToken ? { 'refresh-token': refreshToken } : {}),
            },
        })
        const url = URL.createObjectURL(response.data)
        objectUrlRef.current = url
        setObjectUrl(url)
        return url
    }

    useEffect(() => {
        if (!inline || attachment.unavailable) return
        let cancelled = false
        setLoading(true)
        fetchBlobUrl()
            .catch(() => { if (!cancelled) setFailed(true) })
            .finally(() => { if (!cancelled) setLoading(false) })
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attachment.fetchPath])

    useEffect(() => () => {
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    }, [])

    const download = async () => {
        if (failed || loading) return
        try {
            setLoading(true)
            const url = await fetchBlobUrl()
            const link = document.createElement('a')
            link.href = url
            link.download = attachment.name || 'attachment'
            link.target = '_blank'
            link.rel = 'noopener'
            link.click()
        } catch {
            setFailed(true)
        } finally {
            setLoading(false)
        }
    }

    if (failed) {
        return <FileBubble $sent={sent} $muted>{attachment.name} — no longer available on LinkedIn</FileBubble>
    }

    if (kind === 'image') {
        return objectUrl
            ? <Image src={objectUrl} alt={attachment.name} onClick={() => window.open(objectUrl, '_blank')} />
            : <ImagePlaceholder />
    }

    if (kind === 'audio' && objectUrl) {
        return <audio controls src={objectUrl} style={{ marginBottom: 6, maxWidth: 260 }} />
    }

    return (
        <FileBubble $sent={sent} as="button" type="button" onClick={download}>
            {loading ? 'Downloading…' : attachment.name}
            {attachment.byteSize > 0 && <Size> ({(attachment.byteSize / 1000).toFixed(2)} KB)</Size>}
        </FileBubble>
    )
}

const Image = styled.img`
    max-width: 280px;
    max-height: 280px;
    border-radius: 10px;
    margin-bottom: 6px;
    display: block;
    cursor: pointer;
`
const ImagePlaceholder = styled.div`
    width: 180px;
    height: 120px;
    border-radius: 10px;
    margin-bottom: 6px;
    background: #eef1f6;
`
const FileBubble = styled.div<{ $sent: boolean; $muted?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 6px;
    padding: 8px 12px;
    border: 0;
    border-radius: 10px;
    background: ${({ $sent }) => ($sent ? '#e9e9ff' : '#ffffff')};
    color: ${({ $muted }) => ($muted ? '#8c96ad' : '#41495e')};
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    text-align: left;
    cursor: ${({ $muted }) => ($muted ? 'default' : 'pointer')};
`
const Size = styled.span`
    font-weight: 400;
    color: #8c96ad;
`

export default SharedAttachment
